// =========================
// AdminDashboard — v1.2.0 Workstream 4 (Admin Portal)
//
// Tabbed shell hosting the admin control panels. Each tab lazily mounts its
// own panel component, so only the active panel's data loader fires:
//   • Data Sources — DataSourcePanel    (toggle data sources on/off)
//   • Users        — UserManagementPanel (list users, manual tier override)
//   • Feature Flags— FeatureFlagsPanel   (toggle flags + tier scope)
//   • Pricing      — PricingControlsPanel (read-only tier pricing + Stripe link)
//
// Route is guarded at the App.js level:
//   ProtectedRoute > AdminRoute > Layout > AdminDashboard
// AdminRoute restricts the UI to REACT_APP_ADMIN_EMAILS; the admin-* Netlify
// functions independently enforce the same gate server-side via
// requireAdminFromBearer (defense-in-depth).
// =========================

import React, { useState } from 'react';
import DataSourcePanel from '../components/admin/DataSourcePanel';
import UserManagementPanel from '../components/admin/UserManagementPanel';
import FeatureManagementPanel from '../components/admin/FeatureManagementPanel';
import PricingControlsPanel from '../components/admin/PricingControlsPanel';
import AudiencePanel from '../components/admin/AudiencePanel';
import FunnelPanel from '../components/admin/FunnelPanel';
import SupportInboxPanel from '../components/admin/SupportInboxPanel';

const TABS = [
  { key: 'sources',  label: 'Data Sources',  Panel: DataSourcePanel },
  { key: 'users',    label: 'Users',         Panel: UserManagementPanel },
  { key: 'audience', label: 'Audience',      Panel: AudiencePanel },
  { key: 'funnel',   label: 'Funnel',        Panel: FunnelPanel },
  { key: 'support',  label: 'Support',       Panel: SupportInboxPanel },
  { key: 'features', label: 'Features',      Panel: FeatureManagementPanel },
  { key: 'pricing',  label: 'Pricing',       Panel: PricingControlsPanel },
];

export default function AdminDashboard() {
  const [activeKey, setActiveKey] = useState(TABS[0].key);
  const active = TABS.find((t) => t.key === activeKey) || TABS[0];
  const ActivePanel = active.Panel;

  return (
    <div
      style={{
        background: 'var(--color-navy)',
        minHeight: '100%',
        padding: 'var(--space-6)',
        color: 'var(--color-white)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <header style={{ marginBottom: 'var(--space-5)' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              textTransform: 'uppercase',
              color: 'var(--color-white)',
              margin: 0,
              marginBottom: 'var(--space-2)',
            }}
          >
            Admin
          </h1>
          <p
            style={{
              fontSize: 'var(--text-md)',
              color: 'var(--color-steel-lt)',
              margin: 0,
              lineHeight: 'var(--leading-normal)',
            }}
          >
            Operational controls — no code deploy required. Changes apply on the
            next request.
          </p>
        </header>

        <div
          role="tablist"
          aria-label="Admin panels"
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            borderBottom: '1px solid var(--color-steel-dk)',
            marginBottom: 'var(--space-5)',
            flexWrap: 'wrap',
          }}
        >
          {TABS.map((tab) => {
            const selected = tab.key === activeKey;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveKey(tab.key)}
                className={selected ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{
                  borderTopLeftRadius: 'var(--radius-md)',
                  borderTopRightRadius: 'var(--radius-md)',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div role="tabpanel">
          <ActivePanel />
        </div>
      </div>
    </div>
  );
}
